import {useEffect, useRef} from 'react'
/**
 * The header wrapper is owned by the Rails layout and lives *outside* this React
 * partial, so it is resolved from the ancestry at runtime.
 */
const HEADER_WRAPPER_SELECTOR = '.js-header-wrapper'

function debounceWithFrame(callback: () => void): () => void {
  let frame = 0
  return () => {
    if (frame) cancelAnimationFrame(frame)
    frame = requestAnimationFrame(callback)
  }
}

/** Offsets the fixed full-screen mobile menu below everything above it (banner + header bar). */
function applyMenuPaddingTop(header: HTMLElement, headerMenu: HTMLElement, headerWrapper: HTMLElement | null): void {
  const headerPosition = getComputedStyle(header).position
  const headerIsFixed = headerPosition === 'fixed'
  const headerWrapperIsFixed = headerWrapper ? getComputedStyle(headerWrapper).position === 'fixed' : false

  let height = (headerWrapper ?? header).clientHeight
  // An absolute overlay header sits over the hero, and a `fixed` header is lifted out of the wrapper's
  // flow — both are excluded from the wrapper measurement, so add the header height back.
  if (headerPosition === 'absolute' || (headerIsFixed && headerWrapper)) height += header.clientHeight
  // A viewport-fixed header (or fixed wrapper) never scrolls away, so the menu must clear its full
  // height regardless of scroll position; other headers only clear the portion still on screen.
  if (!headerWrapperIsFixed && !headerIsFixed) height -= window.scrollY
  headerMenu.style.setProperty('padding-top', `${height}px`)
}

/**
 * Measures the height of the wrapper's in-flow content that sits *above* the header, so a `fixed`
 * header can be offset below it (staff bar + banners) instead of pinning to `top: 0` and overlapping
 * it. The header's wrapper-level branch reports that height directly via `offsetTop` (relative to the
 * positioned wrapper), which naturally excludes out-of-flow siblings (the fixed progress bar, the
 * visually-hidden skip link) and any content below the header (e.g. logged-in global notices).
 */
function measureContentAboveHeader(header: HTMLElement, headerWrapper: HTMLElement): number {
  const headerBranch = Array.from(headerWrapper.children).find(child => child.contains(header))
  return headerBranch instanceof HTMLElement ? headerBranch.offsetTop : 0
}

type UseNavPaddingRecalculationArgs = {
  headerRef: React.RefObject<HTMLElement | null>
  headerMenuRef: React.RefObject<HTMLElement | null>
  toggleRef: React.RefObject<HTMLButtonElement | null>
  isOpen: boolean
}

/**
 * Reimplements the legacy nav padding/height recalculation: keeps the
 * `--header-wrapper-height` CSS var in sync via a `ResizeObserver` (only when the
 * wrapper is fixed, and adding the header's own height when the header itself is
 * fixed and thus out of the wrapper's flow) and sets `padding-top` on the fixed
 * `.HeaderMenu` so it clears the header bar.
 */
export function useNavPaddingRecalculation({
  headerRef,
  headerMenuRef,
  toggleRef,
  isOpen,
}: UseNavPaddingRecalculationArgs): void {
  const isOpenRef = useRef(isOpen)
  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  useEffect(() => {
    const header = headerRef.current
    const headerMenu = headerMenuRef.current
    const toggle = toggleRef.current
    if (!header || !headerMenu) return

    const headerWrapper = header.closest<HTMLElement>(HEADER_WRAPPER_SELECTOR)

    const recalculateNavPadding = () => applyMenuPaddingTop(header, headerMenu, headerWrapper)
    const recalculateIfMenuNotOpened = () => {
      if (!isOpenRef.current) recalculateNavPadding()
    }
    const recalculateIfMenuOpened = () => {
      if (isOpenRef.current) recalculateNavPadding()
    }

    let resizeObserver: ResizeObserver | undefined
    if (headerWrapper && getComputedStyle(headerWrapper).position === 'fixed') {
      const recalculateNavHeightVariable = () => {
        let height = headerWrapper.clientHeight
        // A `fixed` header is lifted out of the wrapper's flow, so the wrapper measurement omits it
        // (often leaving only a banner or zero). Add the header height back so consumers that reserve
        // space via `--header-wrapper-height` (e.g. `#hero`) still clear it instead of rendering behind.
        if (getComputedStyle(header).position === 'fixed') {
          height += header.clientHeight
          // Offset the fixed header below the content it shares the fixed wrapper with (staff bar +
          // banners); without this it pins to `top: 0` and overlaps them.
          const contentAboveHeader = measureContentAboveHeader(header, headerWrapper)
          header.style.setProperty('--marketing-header-fixed-top', `${contentAboveHeader}px`)
        }
        document.documentElement.style.setProperty('--header-wrapper-height', `${height}px`)
      }
      const recalculateNavHeightVariableDebounced = debounceWithFrame(recalculateNavHeightVariable)
      resizeObserver = new ResizeObserver(() => recalculateNavHeightVariableDebounced())
      resizeObserver.observe(headerWrapper)
      // Observe the header too: once it is `fixed` it no longer resizes the wrapper, so its own size
      // changes would otherwise never update the variable.
      resizeObserver.observe(header)
      recalculateNavHeightVariable()
    }

    // Pre-compute the padding before the menu opens, on first toggle interaction.
    toggle?.addEventListener('mousedown', recalculateIfMenuNotOpened)
    toggle?.addEventListener('touchstart', recalculateIfMenuNotOpened, {passive: true})
    toggle?.addEventListener('keydown', recalculateIfMenuNotOpened)

    // Recalculate when the global banner closes while the menu is open, since that
    // changes the height above the fixed menu (`global-banner:close` is owned by the banner epic).
    document.addEventListener('global-banner:close', recalculateIfMenuOpened)

    return () => {
      resizeObserver?.disconnect()
      toggle?.removeEventListener('mousedown', recalculateIfMenuNotOpened)
      toggle?.removeEventListener('touchstart', recalculateIfMenuNotOpened)
      toggle?.removeEventListener('keydown', recalculateIfMenuNotOpened)
      document.removeEventListener('global-banner:close', recalculateIfMenuOpened)
    }
  }, [headerRef, headerMenuRef, toggleRef])

  useEffect(() => {
    if (!isOpen) return
    const header = headerRef.current
    const headerMenu = headerMenuRef.current
    if (!header || !headerMenu) return

    const headerWrapper = header.closest<HTMLElement>(HEADER_WRAPPER_SELECTOR)
    const recalculateNavPadding = () => applyMenuPaddingTop(header, headerMenu, headerWrapper)
    const recalculateNavPaddingDebounced = debounceWithFrame(recalculateNavPadding)

    recalculateNavPadding()
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', recalculateNavPaddingDebounced)
    return () => window.removeEventListener('resize', recalculateNavPaddingDebounced)
  }, [isOpen, headerRef, headerMenuRef])
}
